'use client';

import { Flex, Heading, Select } from '@/_components';
import { useContext, useMemo, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { ClientDashboardContext } from '@/_contexts/client/ClientDashboardProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { useParams, useRouter } from 'next/navigation';
import type { Company } from '@/_types/models/company';
import type { EntityYearSelectorProps } from './EntityYearSelector.types';

// Logic from LeftNavEntitySelector.tsx, but adapted for the year selector
export const EntityYearSelector: FC<EntityYearSelectorProps> = ({
  isPreview = false,
}) => {
  const { companies, selectedCompanyId, updateClientCompany } =
    useContext(UserContext);
  const { previewUserCompanies, previewUserCompanyId } =
    useContext(UserPreviewContext);
  const { setYearFilter, yearFilter, years } = useContext(
    ClientDashboardContext,
  );
  const { setPreviewYearFilter, previewYearFilter } =
    useContext(UserPreviewContext);
  const { push } = useRouter();
  const { previewUserId } = useParams();

  const dashboardCompanies: Array<Company> = useMemo(
    () => (isPreview ? previewUserCompanies : companies || []),
    [isPreview, previewUserCompanies, companies],
  );

  const dashboardSelectedCompanyId = useMemo(
    () => (isPreview ? previewUserCompanyId : selectedCompanyId),
    [isPreview, previewUserCompanyId, selectedCompanyId],
  );

  const realYearFilter = useMemo(
    () => (isPreview ? previewYearFilter : yearFilter),
    [isPreview, previewYearFilter, yearFilter],
  );

  const entityItems = useMemo(() => {
    return dashboardCompanies.map((company) => ({
      name: company.name,
      value: company.id.toString(),
    }));
  }, [dashboardCompanies]);

  const onEntitySelect = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedEntityId = parseInt(event.target.value);
    const selectedCompany = dashboardCompanies.find(
      (c) => c.id === selectedEntityId,
    );

    if (selectedCompany && !isPreview) {
      await updateClientCompany(selectedCompany);
    } else if (selectedCompany && isPreview) {
      push(
        `/preview/user/${previewUserId}?previewCompanyHash=${selectedCompany.hash_id}`,
      );
    }
  };

  const onYearSelect = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    return isPreview ? setPreviewYearFilter(newValue) : setYearFilter(newValue);
  };

  return (
    <Flex gap={4} isYCentered={true} data-id="LeftNavEntitySelector.Selector">
      {dashboardCompanies?.length > 1 && (
        <Flex gap={4} isYCentered={true}>
          <Heading level={6} isSameLine={true}>
            Entity:
          </Heading>
          <Select
            label="Entity"
            isDisabled={!dashboardCompanies?.length}
            isLabelHidden={true}
            onChange={onEntitySelect}
            selectedValue={dashboardSelectedCompanyId?.toString() || ''}
            items={entityItems}
          />
        </Flex>
      )}
      <Flex gap={4} isYCentered={true}>
        <Heading level={6} isSameLine={true}>
          Tax Year:
        </Heading>
        <Select
          label="Tax Year"
          isDisabled={!years?.length}
          isLabelHidden={true}
          onChange={onYearSelect}
          selectedValue={realYearFilter}
          items={years}
        />
      </Flex>
    </Flex>
  );
};
